import React, { useEffect } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PolicyHero } from '../components/PolicyHero';
import { ShieldCheck } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { POLICIES, POLICY_LINKS } from '../content/policies';

const md = {
  h2: (p) => <h2 className="font-heading text-xl md:text-2xl font-bold text-white mt-12 mb-4 scroll-mt-24" {...p} />,
  h3: (p) => <h3 className="font-heading text-lg font-semibold text-navigate-gold mt-8 mb-3" {...p} />,
  p: (p) => <p className="text-zinc-300 leading-relaxed mb-4 whitespace-pre-line" {...p} />,
  ul: (p) => <ul className="list-disc pl-6 space-y-1.5 text-zinc-300 mb-4 marker:text-navigate-gold" {...p} />,
  table: (p) => <div className="overflow-x-auto mb-6 rounded-xl border border-zinc-800"><table className="w-full text-sm" {...p} /></div>,
  th: (p) => <th className="text-left bg-zinc-900 text-white font-semibold px-4 py-3 border-b border-zinc-800" {...p} />,
  td: (p) => <td className="align-top px-4 py-3 text-zinc-300 border-b border-zinc-800/60" {...p} />,
  ol: (p) => <ol className="list-decimal pl-6 space-y-1.5 text-zinc-300 mb-4" {...p} />,
  strong: (p) => <strong className="text-white font-semibold" {...p} />,
  a: ({ href, ...p }) => href?.startsWith('/')
    ? <Link to={href} className="text-navigate-gold hover:underline" {...p} />
    : <a href={href} target="_blank" rel="noopener noreferrer" className="text-navigate-gold hover:underline" {...p} />,
};

export default function PolicyPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/+|\/+$/g, '');
  const policy = POLICIES[slug];

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!policy) return <Navigate to="/" replace />;

  return (
    <Layout breadcrumbItems={[{ label: 'Home', href: '/', isRoot: true }, { label: policy.title, isCurrent: true }]}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid={`policy-page-${slug}`}>
        <nav className="flex flex-wrap gap-2 mb-10" data-testid="policy-nav">
          {POLICY_LINKS.map((l) => (
            <Link key={l.slug} to={`/${l.slug}`} data-testid={`policy-nav-${l.slug}`}
              className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${l.slug === slug
                ? 'bg-navigate-gold text-navigate-dark border-navigate-gold font-semibold'
                : 'border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500'}`}>
              {l.label}
            </Link>
          ))}
        </nav>
        {policy.hero ? <PolicyHero policy={policy} slug={slug} /> : (
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck className="h-8 w-8 text-navigate-gold" />
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white" data-testid="policy-title">{policy.title}</h1>
          </div>
        )}
        <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-zinc-400 pb-6 border-b border-zinc-800" data-testid="policy-meta">
          {policy.meta.map(([k, v]) => (
            <div key={k}><dt className="inline text-zinc-500">{k}: </dt><dd className="inline text-zinc-300">{v}</dd></div>
          ))}
        </dl>
        <article className="mt-8" data-testid="policy-body">
          {policy.body
            ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={md}>{policy.body}</ReactMarkdown>
            : <p className="text-zinc-400" data-testid="policy-coming-soon">Our Responsible AI statement is being finalised and will be published here shortly.</p>}
        </article>
      </div>
    </Layout>
  );
}
